export class EntityIconError extends Error {
    statusCode = 400;
    constructor(message) {
        super(message);
        this.name = "EntityIconError";
    }
}
export const REACT_ICON_PACKS = [
    "fa6",
    "hi2",
    "io5",
    "lia",
    "tfi",
    "vsc",
    "ai",
    "bi",
    "bs",
    "cg",
    "ci",
    "di",
    "fa",
    "fc",
    "fi",
    "gi",
    "go",
    "gr",
    "hi",
    "im",
    "io",
    "lu",
    "md",
    "pi",
    "ri",
    "rx",
    "si",
    "sl",
    "tb",
    "ti",
    "wi",
];
const PACK_PREFIXES = [
    { prefix: "Fa6", pack: "fa6" },
    { prefix: "Hi2", pack: "hi2" },
    { prefix: "Io5", pack: "io5" },
    { prefix: "Lia", pack: "lia" },
    { prefix: "Tfi", pack: "tfi" },
    { prefix: "Vsc", pack: "vsc" },
    { prefix: "Ai", pack: "ai" },
    { prefix: "Bi", pack: "bi" },
    { prefix: "Bs", pack: "bs" },
    { prefix: "Cg", pack: "cg" },
    { prefix: "Ci", pack: "ci" },
    { prefix: "Di", pack: "di" },
    { prefix: "Fa", pack: "fa" },
    { prefix: "Fc", pack: "fc" },
    { prefix: "Fi", pack: "fi" },
    { prefix: "Gi", pack: "gi" },
    { prefix: "Go", pack: "go" },
    { prefix: "Gr", pack: "gr" },
    { prefix: "Hi", pack: "hi" },
    { prefix: "Im", pack: "im" },
    { prefix: "Io", pack: "io" },
    { prefix: "Lu", pack: "lu" },
    { prefix: "Md", pack: "md" },
    { prefix: "Pi", pack: "pi" },
    { prefix: "Ri", pack: "ri" },
    { prefix: "Rx", pack: "rx" },
    { prefix: "Si", pack: "si" },
    { prefix: "Sl", pack: "sl" },
    { prefix: "Tb", pack: "tb" },
    { prefix: "Ti", pack: "ti" },
    { prefix: "Wi", pack: "wi" },
];
const REACT_NAME = /^[A-Z][A-Za-z0-9]{1,80}$/;
const IMAGE_URL = /^\/api\/guest-icons\/(group|party|member)\/\d+(\?v=\d+)?$/;
const SVG_TAGS = new Set([
    "svg",
    "g",
    "path",
    "circle",
    "rect",
    "ellipse",
    "line",
    "polyline",
    "polygon",
    "title",
]);
const SVG_ATTRS = new Set([
    "viewbox",
    "xmlns",
    "fill",
    "fill-opacity",
    "fill-rule",
    "stroke",
    "stroke-width",
    "stroke-linecap",
    "stroke-linejoin",
    "stroke-miterlimit",
    "stroke-opacity",
    "d",
    "points",
    "transform",
    "opacity",
    "x",
    "y",
    "x1",
    "y1",
    "x2",
    "y2",
    "cx",
    "cy",
    "r",
    "rx",
    "ry",
    "width",
    "height",
]);
export function reactIconPack(name) {
    if (!REACT_NAME.test(name)) {
        throw new EntityIconError("React icon name is not valid");
    }
    const match = PACK_PREFIXES.find((entry) => {
        if (!name.startsWith(entry.prefix) || name.length <= entry.prefix.length)
            return false;
        const next = name[entry.prefix.length];
        return next != null && next >= "A" && next <= "Z";
    });
    if (!match)
        throw new EntityIconError("React icon name is not valid");
    return match.pack;
}
function assertEmoji(value) {
    if (typeof value !== "string")
        throw new EntityIconError("Emoji is not valid");
    const text = value.trim();
    if (!text || text.length > 32 || [...text].length > 16) {
        throw new EntityIconError("Emoji is not valid");
    }
    if (/[<>\u0000-\u001F\u007F]/.test(text)) {
        throw new EntityIconError("Emoji is not valid");
    }
    return text;
}
function assertSvg(source) {
    if (typeof source !== "string")
        throw new EntityIconError("SVG is not allowed");
    const svg = source.trim();
    if (!svg || svg.length > 20_000)
        throw new EntityIconError("SVG is not allowed");
    if (/<!|<\?|<!--/i.test(svg))
        throw new EntityIconError("SVG is not allowed");
    if (/<script|<foreignobject|javascript:|\son[a-z]+\s*=/i.test(svg)) {
        throw new EntityIconError("SVG is not allowed");
    }
    let index = 0;
    const stack = [];
    let roots = 0;
    const skipSpace = () => {
        while (index < svg.length && /\s/.test(svg[index] ?? ""))
            index += 1;
    };
    while (index < svg.length) {
        skipSpace();
        if (index >= svg.length)
            break;
        if (svg[index] !== "<") {
            if (stack[stack.length - 1] !== "title")
                throw new EntityIconError("SVG is not allowed");
            const next = svg.indexOf("<", index);
            const text = svg.slice(index, next === -1 ? svg.length : next);
            if (text.length > 200 || /[<>]/.test(text))
                throw new EntityIconError("SVG is not allowed");
            index = next === -1 ? svg.length : next;
            continue;
        }
        const closing = svg[index + 1] === "/";
        index += closing ? 2 : 1;
        const nameMatch = /^([A-Za-z][A-Za-z0-9]*)/.exec(svg.slice(index));
        if (!nameMatch?.[1])
            throw new EntityIconError("SVG is not allowed");
        const tag = nameMatch[1].toLowerCase();
        if (!SVG_TAGS.has(tag))
            throw new EntityIconError("SVG is not allowed");
        index += nameMatch[1].length;
        if (!closing) {
            let selfClosing = false;
            for (;;) {
                skipSpace();
                if (svg.startsWith("/>", index)) {
                    selfClosing = true;
                    index += 2;
                    break;
                }
                if (svg[index] === ">") {
                    index += 1;
                    break;
                }
                const attrMatch = /^([A-Za-z_:][-A-Za-z0-9_:.]*)/.exec(svg.slice(index));
                if (!attrMatch?.[1])
                    throw new EntityIconError("SVG is not allowed");
                const attrName = attrMatch[1].toLowerCase();
                if (attrName.startsWith("on") || !SVG_ATTRS.has(attrName)) {
                    throw new EntityIconError("SVG is not allowed");
                }
                index += attrMatch[1].length;
                skipSpace();
                if (svg[index] !== "=")
                    throw new EntityIconError("SVG is not allowed");
                index += 1;
                skipSpace();
                const quote = svg[index];
                if (quote !== '"' && quote !== "'")
                    throw new EntityIconError("SVG is not allowed");
                index += 1;
                const end = svg.indexOf(quote, index);
                if (end < 0)
                    throw new EntityIconError("SVG is not allowed");
                const attrValue = svg.slice(index, end);
                if (/url\s*\(|javascript:|expression\s*\(|[\u0000-\u001F]/i.test(attrValue)) {
                    throw new EntityIconError("SVG is not allowed");
                }
                if (attrName === "xmlns" && attrValue !== "http://www.w3.org/2000/svg") {
                    throw new EntityIconError("SVG is not allowed");
                }
                if (attrName === "transform" && !/^[\w\s(),.+\-]+$/.test(attrValue)) {
                    throw new EntityIconError("SVG is not allowed");
                }
                index = end + 1;
            }
            if (tag === "svg") {
                if (stack.length !== 0)
                    throw new EntityIconError("SVG is not allowed");
                roots += 1;
            }
            else if (stack.length === 0) {
                throw new EntityIconError("SVG is not allowed");
            }
            if (!selfClosing)
                stack.push(tag);
        }
        else {
            skipSpace();
            if (svg[index] !== ">")
                throw new EntityIconError("SVG is not allowed");
            index += 1;
            if (stack.pop() !== tag)
                throw new EntityIconError("SVG is not allowed");
        }
    }
    if (stack.length !== 0 || roots !== 1)
        throw new EntityIconError("SVG is not allowed");
    return svg;
}
function asRecord(value) {
    if (!value || typeof value !== "object" || Array.isArray(value)) {
        throw new EntityIconError("Icon is not valid");
    }
    return value;
}
/** Accepts null. Throws EntityIconError for any other value that is not an icon. */
export function parseEntityIcon(value) {
    if (value == null)
        return null;
    const record = asRecord(value);
    const type = record["type"];
    if (type === "emoji") {
        return { type: "emoji", value: assertEmoji(record["value"]) };
    }
    if (type === "react") {
        if (typeof record["name"] !== "string") {
            throw new EntityIconError("React icon name is not valid");
        }
        const pack = reactIconPack(record["name"]);
        if (record["pack"] != null && record["pack"] !== pack) {
            throw new EntityIconError("React icon name is not valid");
        }
        return { type: "react", pack, name: record["name"] };
    }
    if (type === "svg") {
        return { type: "svg", value: assertSvg(record["value"]) };
    }
    if (type === "image") {
        if (typeof record["url"] !== "string" || !IMAGE_URL.test(record["url"])) {
            throw new EntityIconError("Image icon path is not valid");
        }
        return { type: "image", url: record["url"] };
    }
    throw new EntityIconError("Icon is not valid");
}
/** Database read. Invalid stored JSON becomes no icon so one bad row does not fail a list. */
export function readEntityIcon(value) {
    if (value == null)
        return null;
    let parsed = value;
    if (typeof value === "string") {
        const trimmed = value.trim();
        if (!trimmed)
            return null;
        try {
            parsed = JSON.parse(trimmed);
        }
        catch {
            return null;
        }
    }
    try {
        return parseEntityIcon(parsed);
    }
    catch {
        return null;
    }
}
export function entityIconJson(icon) {
    if (icon == null)
        return null;
    return JSON.stringify(parseEntityIcon(icon));
}
/** Group copy keeps every kind except an uploaded file, which belongs to one row. */
export function copyableEntityIcon(icon) {
    if (!icon || icon.type === "image")
        return null;
    return icon;
}
//# sourceMappingURL=entityIcon.js.map